import type { ReactNode } from 'react';
import { memo } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Link } from 'react-router-dom';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';

import { Media } from '../../../../../theme/Media.tsx';
import { NotificationIndicator } from '../../../../Elements/NotificationIndicator/index.tsx';
import { usePuzzleNavigationContext } from '../../context.tsx';
import { usePathMatch } from '../../hooks/index.ts';
import type { RibbonVariant } from '../../types.ts';
import { CornerBadge } from '../CornerBadge.tsx';
import { TagContainer, Wrapper } from './styled.ts';

interface ItemSubLinkBaseProps {
  title: string;
  exact?: boolean;
  path?: string | string[];
  dataTestId?: string;
  tag?: ReactNode;
  ribbon?: RibbonVariant;
  showIndicator?: boolean;
}

interface ItemSubLinkProps extends ItemSubLinkBaseProps {
  to: string;
  onClick?: never;
}

interface ItemSubLinkButtonProps extends ItemSubLinkBaseProps {
  to?: never;
  onClick: () => void;
}

export type ItemProps = ItemSubLinkProps | ItemSubLinkButtonProps;

const ItemSubLink = ({ title, exact, path, to, onClick, dataTestId, tag, ribbon, showIndicator }: ItemProps) => {
  const match = usePathMatch({ path: path ?? to, exact });
  const isMobile = useMediaQuery({ query: Media.xsMax });

  const { collapseMainNav } = usePuzzleNavigationContext();

  const handleCloseMenu = useCallbackRef(() => {
    if (to && isMobile) {
      collapseMainNav();
    }
  });

  return (
    <Wrapper $active={match}>
      {ribbon && <CornerBadge variant={ribbon} />}
      {to && (
        <Link to={to} onClick={handleCloseMenu} data-testid={dataTestId}>
          <span>{title}</span>
          {showIndicator && <NotificationIndicator warning />}
          {tag && <TagContainer>{tag}</TagContainer>}
        </Link>
      )}
      {onClick && (
        <span onClick={onClick} data-testid={dataTestId}>
          <span>{title}</span>
          {showIndicator && <NotificationIndicator warning />}
          {tag && <TagContainer>{tag}</TagContainer>}
        </span>
      )}
    </Wrapper>
  );
};

export default memo(ItemSubLink);
